import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {useTranslation} from 'react-i18next';
import PermissionProvider from '../../components/PermissionProvider/PermissionProvider';
import { Permission } from '../../store/types/Permission';
import DeviceProfileTitle from './DeviceProfileTitle';
import Loader from '../../components/Loader/Loader';
import { Colors } from '../../styles/Colors';
import { actions } from '../../store';
import { useDispatch, useSelector } from 'react-redux';
import { ReduxState } from '../../store/types';
import DetailsWrapper from '../../components/DetailsWraper/DetailsWrapper';
import { generateTabs } from '../../utils/generateTabs';
import { useRawPermissions } from '../../hooks/usePermissions';
import DeviceProfileDetailsTab from './DeviceProfileDetailsTab';
import history from '../../history';
import { Routes } from '../../routes/routes';
import DevicePhoneBookTab from './DevicePhoneBookTab';
import { Formik } from 'formik';
import { DeviceProfileDetailsTabFormType, deviceProfileValidationSchema, mapDeviceProfileDetailsTabFormToSaveObject, useDeviceProfileDetailsInitialDate } from './DeviceProfileDetailsTab.utils';
import { AddOptionsTemplateRequest, OptionsTemplateInfo, UpdateOptionsTemplateRequest } from '../../store/types/Devices';
import { showToast } from '../../utils/showToast';
import { getRoute } from './NewDeviceProfileDialog.utils';
import NotFound from '../NotFound/NotFound';
import { DEVICE_PROFILE_NOT_FOUND_API_ERROR } from '../../store/sagas/devices/saga';
import { getTabNumber } from '../../utils/getTabNumber';

export const useStyle = makeStyles(() => ({
    rootDeviceProfileDetails: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        maxHeight: '100vh',
        height: '100vh',
        width: '100%',
        overflowY: 'hidden',
        backgroundColor: Colors.SmokeBackground,
        position: 'relative'
    },

    deviceProfileHeader: {
        padding: `30px 0px 30px 96px`,
        backgroundColor: Colors.White,
    },
    
    '@media (max-width: 1276px)': {
        deviceHeader: {
            paddingLeft: 24,
            paddingRight: 24,
        },
    },
    
    '@media (min-width:1277px) and (max-width: 1440px)': {
        deviceHeader: {
            paddingLeft: 90,
            paddingRight: 90,
        },
    },

    '@media (min-width: 1441px)': {
        deviceHeader: {
            paddingLeft: 96,
            paddingRight: 96,
        },
    },

    scrollableDeviceProfileDetails: {
        flex: 1,
        height: 'fit-content',
    },
    
    tabsHeader: {
        display: 'none'
    },
    
    loader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        flex: 1,
    },

    customRootClassForDeviceProfilePage: {
        '& > :first-child': {
            overflowY: 'hidden',
            height: 'fit-content',
            maxHeight: 'calc(100vh - 118px)',
            
            '& [role="tabpanel"]': {
                '& > :last-child': {
                    height: 'fit-content !important',
                    overflowY: 'auto !important',
                    maxHeight: 'calc(100vh - 207px)',
                }
            }
        }
    }
}));

export interface DeviceProfileDetailsProps {
    id: number;
    copy?: boolean;
}

const DeviceProfileDetails: React.FC<DeviceProfileDetailsProps> = ({
    id,
    copy
}) => {
    const classes = useStyle();
    const {t} = useTranslation();
    const showLoader = false;
    const dispatch = useDispatch();
    const permissions = useRawPermissions();
    const i_customer: number = useSelector((state: ReduxState) => state.generic?.sessionData?.i_customer ?? 0);

    const {
        profileDetails,
        uaProfileMetaInfo,
        isLoadingMetadata,
        isTypeListLoading,
        isLoadingTemplates,
        isUpdatingOptionsTemplate,
        isLoadingProfileDetails,
        phoneDirectories,
        profileDetailsApiError
    } = useSelector((state: ReduxState) => state.devices);

    const onChevronClick = useCallback(() => {
        history.push(Routes.DeviceProfiles);
    }, []);

    const [tabIndex, setTabIndex] = useState(getTabNumber([0, 1]));

    const handleTabindexChange = (
        event: React.ChangeEvent<{}>,
        index: number,
    ) => setTabIndex(index);
    
    useEffect(() => {
        if (id && profileDetails == undefined && i_customer && !isLoadingProfileDetails && !profileDetailsApiError) {
            dispatch(
                actions.getProfileTemplate.request({
                    i_customer: i_customer,
                    i_ua_config_template: id
                })
            );
        }
    }, [i_customer, id, profileDetails, isLoadingProfileDetails]);

    const isLoading = useMemo(() => {
        if(profileDetailsApiError) {
            return false;
        }
        if(!id) {
            return false;
        }
        return isLoadingMetadata 
            || isTypeListLoading 
            || isLoadingTemplates
            || !i_customer 
            || !profileDetails 
            || isUpdatingOptionsTemplate
            || !uaProfileMetaInfo;
    }, [isLoadingMetadata, isTypeListLoading, isLoadingTemplates, i_customer, 
            profileDetails, isUpdatingOptionsTemplate, uaProfileMetaInfo, profileDetailsApiError]);

    useEffect(() => {
        if(profileDetails?.i_ua_type)
        {
            if(isLoadingMetadata !== undefined || uaProfileMetaInfo) {
                return;
            }
            dispatch(
                actions.getUaProfileMetaInfo.request({
                    i_ua_type: profileDetails.i_ua_type,
                })
            );
        }
    }, [profileDetails, isLoadingMetadata, uaProfileMetaInfo]);

    const {tabNames, tabs} = useMemo(() => generateTabs(
        [
            {
                title: t('screens:deviceProfiles.programmableKeys'),
                permission: Permission.Inventory.DeviceProfiles.value,
                tab: (
                    <DeviceProfileDetailsTab
                        uaProfileMetaInfo={uaProfileMetaInfo}
                        handleOnSave={(a, b) => {
                            handleOnSave(a,b);
                        }}
                    />
                ),
            },
            {
                title: t('screens:devicePhoneBooks.phoneBooksTabHeader'),
                permission: Permission.Inventory.DevicePhoneBooks.value,
                tab: (
                    <DevicePhoneBookTab
                        profile={profileDetails}
                        uaProfileMetaInfo={uaProfileMetaInfo}
                    />
                ),
            }
        ],
        permissions
    ), [profileDetails, uaProfileMetaInfo, i_customer, id]);
    
    const initFormData = useMemo(() => {
        return useDeviceProfileDetailsInitialDate(copy ?? false, profileDetails ?? null, t, uaProfileMetaInfo, phoneDirectories);
    }, [copy, profileDetails, t, uaProfileMetaInfo, phoneDirectories]);

    const handleOnSave = useCallback((form: DeviceProfileDetailsTabFormType, ignoreOnSave = false) => {
        if(!profileDetails) 
            return;

        const options = mapDeviceProfileDetailsTabFormToSaveObject(form);
        if(copy)
        {
            const model: AddOptionsTemplateRequest = {
                template_info: {
                    i_customer: profileDetails.i_customer,
                    i_ua_list: [],
                    i_ua_type: profileDetails.i_ua_type,
                    name: form.profileName,
                    directories_options: form.directories.filter(e => e.enabled)
                        .map(e => { 
                            return {i_ua_config_directory: e.i_ua_config_directory};
                        })
                } as OptionsTemplateInfo,
                callback: (newId: number) => {
                    const payload: UpdateOptionsTemplateRequest = {
                        //@ts-ignore
                        template_info: {
                            i_ua_config_template: newId,
                            keys_options: options,
                            name: form.profileName
                        },
                        callback: () => {
                            showToast(t('screens:deviceProfiles.profileTemplateSaved'));
                            if(!ignoreOnSave) {
                                //onSaveComplete?.();
                            }
                            history.push(
                                getRoute(Routes.DeviceProfilesDetails, {id: newId}),
                            );
                        }
                    };
                    dispatch(actions.updateOptionsTemplate.request([payload]));
                },
                isCloning: true,
            };
            dispatch(actions.addDeviceProfile.request(model));
        }
        else {
            const payload: UpdateOptionsTemplateRequest = {
                //@ts-ignore
                template_info: {
                    i_ua_config_template: profileDetails.i_ua_config_template,
                    keys_options: options,
                    name: form.profileName,
                    directories_options: form.directories.filter(e => e.enabled)
                        .map(e => { 
                            return {i_ua_config_directory: e.i_ua_config_directory};
                        })
                },
                callback: () => {
                    showToast(t('screens:deviceProfiles.profileTemplateUpdated'));
                    if(!ignoreOnSave) {
                        //onSaveComplete?.();
                    }
                    else
                    {
                        history.push(
                            getRoute(Routes.DeviceProfiles, {}),
                        );
                    }
                }
            };
            dispatch(actions.updateOptionsTemplate.request([payload]));
        }

    }, [profileDetails, copy]);

    if (profileDetailsApiError?.faultcode === DEVICE_PROFILE_NOT_FOUND_API_ERROR || !id) {
        return (
            <NotFound />
        );
    }

    return (
        <PermissionProvider permission={Permission.Inventory.DeviceProfiles.value}>
            <Formik<DeviceProfileDetailsTabFormType>
                initialValues={initFormData}
                validationSchema={deviceProfileValidationSchema}
                onSubmit={(form) => {
                    handleOnSave(form);
                }}
                enableReinitialize
                validateOnChange={false}
                validateOnBlur
            >
                {isLoading ? (<div className={classes.loader}>
                    <Loader dataQa={'device-details-loader'}/>
                </div>) : (
                    <div className={classes.rootDeviceProfileDetails}>
                        {(showLoader) && (
                            <Loader
                                dataQa="edit-device-profile-loader"
                                absolutePosition
                            />
                        )}
                        <div className={classes.scrollableDeviceProfileDetails}>
                            <DeviceProfileTitle
                                profile={profileDetails}
                                className={classes.deviceProfileHeader}
                                onChevronClick={onChevronClick}
                                copy={copy ?? false}
                            />
                            
                            <DetailsWrapper
                                showActionButtons={false}
                                tabs={tabs}
                                tabsNames={tabNames}
                                headerCustomClass={classes.tabsHeader}
                                customRootClass={classes.customRootClassForDeviceProfilePage}
                                doNotControlForms
                                onTabChange={handleTabindexChange}
                                defaultTabIndex={tabIndex}
                            >
                            </DetailsWrapper>
                        </div>
                    </div>
                )}
            </Formik>
        </PermissionProvider>
    );
};

export default DeviceProfileDetails;
